import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";
import { SITE } from "@/constants/testIds";

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        setPost(null);
        setError(null);
        api.post(slug)
            .then(setPost)
            .catch((e) => setError(e?.response?.status === 404 ? "notfound" : "error"));
        api.posts(50).then((all) => setRelated(all.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    }, [slug]);

    if (error === "notfound") {
        return (
            <div>
                <PageHeader
                    eyebrow="NOT FOUND"
                    title="Article not found"
                    subtitle="That story may have moved — here are some other places to start."
                    breadcrumbs={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: "Not found" }]}
                >
                    <CtaButton to="/blog" variant="secondary">
                        Back to blog
                    </CtaButton>
                </PageHeader>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="py-24 text-center text-foreground/60">Loading...</div>
        );
    }

    return (
        <div>
            <PageHeader
                eyebrow={post.category?.toUpperCase()}
                title={post.title}
                subtitle={post.excerpt}
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Blog", to: "/blog" },
                    { label: post.title },
                ]}
            />

            <section className="py-10">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-4 text-sm text-foreground/65">
                        <span className="inline-flex items-center gap-1">
                            <Calendar className="h-4 w-4" /> {post.published_at}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Clock className="h-4 w-4" /> {post.read_time}
                        </span>
                        <span>— by {post.author}</span>
                    </div>

                    <div className="mt-6 rounded-3xl overflow-hidden border border-border/70 bg-sand">
                        <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full h-auto object-cover"
                        />
                    </div>

                    <article className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8">
                            <div className="prose-ay max-w-[65ch] drop-cap">
                                {post.content.split("\n\n").map((para, i) => {
                                    const paraKey = `para-${i}-${para.slice(0, 24)}`;
                                    if (para.startsWith("### ")) {
                                        return (
                                            <h3
                                                key={paraKey}
                                                className="font-display text-xl text-moss mt-8 mb-2"
                                            >
                                                {para.replace("### ", "")}
                                            </h3>
                                        );
                                    }
                                    return <p key={paraKey}>{para}</p>;
                                })}
                            </div>

                            <div className="mt-8 flex flex-wrap gap-2">
                                {post.tags?.map((t) => (
                                    <span
                                        key={t}
                                        className="rounded-full bg-sand/80 border border-border/70 px-3 py-1 text-xs text-foreground/70"
                                    >
                                        #{t}
                                    </span>
                                ))}
                            </div>

                            <div className="mt-10 flex items-center justify-between">
                                <Link
                                    to="/blog"
                                    className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-terracotta"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Back to blog
                                </Link>
                                <CtaButton to="/contact" variant="secondary">
                                    Schedule a consultation
                                </CtaButton>
                            </div>
                        </div>

                        <aside className="lg:col-span-4">
                            <div className="sticky top-6 space-y-5">
                                <div className="rounded-2xl border border-border/80 bg-cream p-5">
                                    <p className="font-accent text-xs text-terracotta">CONTINUE READING</p>
                                    <ul className="mt-3 space-y-3">
                                        {related.map((r) => (
                                            <li key={r.slug}>
                                                <Link
                                                    to={`/blog/${r.slug}`}
                                                    data-testid={SITE.blogReadMore}
                                                    className="flex gap-3 group"
                                                >
                                                    <div className="shrink-0 h-14 w-14 rounded-xl overflow-hidden bg-sand">
                                                        <img
                                                            src={r.cover_image}
                                                            alt={r.title}
                                                            className="h-full w-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-foreground/55">{r.read_time}</p>
                                                        <p className="font-display text-sm text-moss group-hover:text-terracotta line-clamp-2">
                                                            {r.title}
                                                        </p>
                                                    </div>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </aside>
                    </article>
                </div>
            </section>
        </div>
    );
}
